import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faSpinner, faArrowsUpDownLeftRight
} from '@fortawesome/free-solid-svg-icons';
import AnnotatableImage from './AnnotatableImage';

// Keep the floating panel below the sticky app header (and Evaluate
// sub-tab when present) so it cannot tuck under the menu bar.
function getDragMinY() {
  let minY = 0;
  if (typeof document === 'undefined') return 56;
  const header = document.querySelector('.app-header');
  if (header) minY = Math.max(minY, header.getBoundingClientRect().bottom);
  const subnav = document.querySelector('[data-evaluate-subnav]');
  if (subnav) minY = Math.max(minY, subnav.getBoundingClientRect().bottom);
  if (minY <= 0) {
    const cssH = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    minY = (Number.isFinite(cssH) ? cssH : 52);
  }
  return Math.ceil(minY) + 4;
}

// Floating, draggable image preview for MCQ rows. Lazily fetches the raw image bytes via the backend
// proxy (/scans/file/:scanId/raw) and caches them as blob: URLs per
// scan_id. Using blob URLs (not the cross-origin S3 presigned link)
// keeps the html2canvas-driven Save / Copy buttons inside
// AnnotatableImage clean — without the proxy step the canvas would be
// "tainted" by the cross-origin image and the annotation save would
// produce only the SVG layer on a black background.
//
// NON-MODAL by design: there is no full-screen backdrop, so the OMR
// answers table behind the panel stays fully interactive — the grader can
// edit table cells while comparing them to the scan. Grab the dark title
// bar to drag the whole panel anywhere on screen; the image area keeps its
// own scroll-zoom / drag-pan, so the two gestures never fight.
//
// Props:
//   items: [{ scan_id, file_name, label }]   // label e.g. "OMR sheet", "Text page"
//   initialIndex: number                     // which item to open first
//   onClose: () => void
//
// Keyboard: Esc closes, ←/→ flip between items — but ONLY when focus isn't
// inside an input/textarea/select/contentEditable, so typing into a table
// cell is never hijacked.
export default function MCQImagePreview({ items, initialIndex = 0, onClose, width = '45vw', dockRight = false }) {
  const [idx, setIdx] = useState(initialIndex);
  // urlCache: { [scan_id]: { url, loading, error } }
  // `url` here is a blob: URL pointing at bytes fetched from the
  // backend proxy. Cached so flipping back and forth doesn't refetch.
  const [urlCache, setUrlCache] = useState({});

  // Floating-panel position (top-left corner, in viewport px). Defaults to
  // the right portion of the screen, just below the app header, so it
  // doesn't cover the table's leftmost (name / roll) columns.
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const minY = getDragMinY();
    // dockRight: hug the right edge using the panel's own width (parsed from
    // the `width` prop when it's a vw value). Otherwise the legacy center-right.
    if (dockRight) {
      const vw = String(width).match(/^([\d.]+)vw$/);
      const panelPx = vw ? (parseFloat(vw[1]) / 100) * w : w * 0.45;
      return { x: Math.max(12, Math.round(w - panelPx - 12)), y: minY };
    }
    return { x: Math.max(12, Math.round(w * 0.46)), y: minY };
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null); // { sx, sy, ox, oy } captured on drag start

  const API_BASE_URL = window.API_BASE_URL;
  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  // Fetch the bytes for the currently-shown item if we don't have it
  // yet. Goes through the same-origin /raw proxy so the resulting blob
  // URL isn't cross-origin and the canvas stays clean.
  useEffect(() => {
    const cur = items[idx];
    if (!cur || !cur.scan_id) return;
    if (urlCache[cur.scan_id]) return;
    setUrlCache(prev => ({ ...prev, [cur.scan_id]: { loading: true } }));
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/scans/file/${cur.scan_id}/raw`, { headers: authHeaders() });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setUrlCache(prev => ({ ...prev, [cur.scan_id]: { url } }));
      } catch (err) {
        setUrlCache(prev => ({ ...prev, [cur.scan_id]: { error: err.message } }));
      }
    })();
  }, [idx, items, urlCache, API_BASE_URL, authHeaders]);

  // Revoke every blob URL on unmount so they don't pile up in memory.
  // Per-scan cleanup isn't needed because the cache is invalidated when
  // the modal closes (component unmounts).
  useEffect(() => {
    return () => {
      Object.values(urlCache).forEach(entry => {
        if (entry?.url && entry.url.startsWith('blob:')) URL.revokeObjectURL(entry.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard nav. Skip entirely when the user is typing into a form field
  // (editing the OMR table) so arrow keys and Esc keep working there.
  useEffect(() => {
    const isEditable = (el) =>
      !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    const onKey = (e) => {
      if (isEditable(e.target)) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1);
      else if (e.key === 'ArrowRight' && idx < items.length - 1) setIdx(i => i + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, items.length, onClose]);

  // Window-drag: grabbing the title bar records the offset, then global
  // mousemove updates the panel position until mouseup. Listening on
  // window (not the panel) means the drag survives the cursor leaving the
  // title bar. A soft clamp keeps the panel from being lost off-screen.
  const onDragStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    setDragging(true);
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      let nx = d.ox + (e.clientX - d.sx);
      let ny = d.oy + (e.clientY - d.sy);
      const minX = -(window.innerWidth * 0.45) + 120; // keep title bar partly visible
      const maxX = window.innerWidth - 60;
      const minY = getDragMinY();
      const maxY = window.innerHeight - 40;
      nx = Math.min(maxX, Math.max(minX, nx));
      ny = Math.min(maxY, Math.max(minY, ny));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  if (!items || items.length === 0) return null;
  const cur = items[idx] || {};
  const cache = urlCache[cur.scan_id] || {};

  // Stop a click/drag inside the panel from bubbling to anything below.
  const stop = (e) => e.stopPropagation();

  // Title-bar caption: filename + position when there's more than one item.
  const captionText = (() => {
    const base = cur.file_name || `Scan ${cur.scan_id}`;
    if (items.length > 1) return `${base} · Image ${idx + 1} of ${items.length}`;
    return base;
  })();

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: width,
        maxWidth: '96vw',
        zIndex: 100000,
        // No backdrop on purpose — the page (and the OMR table) stays
        // interactive. Pointer events only land on this floating panel.
        filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.45))'
      }}
    >
      {/* Drag handle / title bar — grab here to move the whole panel. */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(17,24,39,0.92)',
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
      >
        <FontAwesomeIcon icon={faArrowsUpDownLeftRight} style={{ opacity: 0.85, fontSize: 12 }} />
        <span style={{
          flex: 1,
          textAlign: 'center',
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {captionText}
        </span>
        <button
          onClick={onClose}
          onMouseDown={stop}
          title="Close (Esc)"
          style={{
            background: '#EF4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: 24,
            height: 24,
            lineHeight: '24px',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0
          }}
        >×</button>
      </div>

      {/* Image area — position:relative so the nav arrows anchor to the
          panel (not the viewport) and travel with it when dragged. */}
      <div style={{
        position: 'relative',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        overflow: 'hidden'
      }}>
        {cache.loading || !cache.url
          ? (
            <div style={{
              width: '100%', height: '80vh',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 10, color: '#374151',
              background: 'rgba(255,255,255,0.94)'
            }}>
              {cache.error
                ? <span style={{ color: '#b91c1c' }}>Failed to load image: {cache.error}</span>
                : <><FontAwesomeIcon icon={faSpinner} spin size="2x" /> Loading…</>}
            </div>
          )
          : (
            <AnnotatableImage
              url={cache.url}
              alt={cur.file_name || 'scan'}
              width={width}
              height="80vh"
              background="transparent"
            />
          )
        }

        {/* Nav arrows — absolute within the panel so they move with it. */}
        {items.length > 1 && idx > 0 && (
          <button
            onMouseDown={stop}
            onClick={() => setIdx(i => i - 1)}
            title="Previous (←)"
            style={navBtnStyle('left')}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}
        {items.length > 1 && idx < items.length - 1 && (
          <button
            onMouseDown={stop}
            onClick={() => setIdx(i => i + 1)}
            title="Next (→)"
            style={navBtnStyle('right')}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
    </div>
  );
}

const navBtnStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 10,
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.4)',
  background: 'rgba(0,0,0,0.55)',
  color: 'white',
  cursor: 'pointer',
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
  boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
});
